import { z } from "zod"
import { USER_ROLES, USER_PERMISSIONS } from "@shared/constants/user-constants"

// Role schema aligned with backend role strings
export const RoleSchema = z.union([
	z.literal(USER_ROLES.SUPERADMIN),
	z.literal(USER_ROLES.ADMIN),
	z.literal(USER_ROLES.USER),
	z.literal(USER_ROLES.VIEWER),
])

export const CreateUserSchema = z.object({
	userName: z.string().min(3, "Username must be at least 3 characters"),
	email: z.string().email("Please enter a valid email address"),
	fullName: z.string().min(1, "Full name is required"),
	tenantId: z.string().min(1, "Organization is required"),
	password: z.string().min(8, "Password must be at least 8 characters"),
	role: RoleSchema.optional(),
	emailConfirmed: z.boolean().optional(),
	lockoutEnabled: z.boolean().optional(),
	active: z.boolean().optional(),
	permissions: z.array(z.nativeEnum(USER_PERMISSIONS as unknown as Record<string, string>)).optional(),
	workstationIds: z.array(z.number()).optional(),
})

// All fields optional, but require at least one to update
export const UpdateUserSchema = z.object({
	userName: z.string().min(3).optional(),
	email: z.string().email().optional(),
	fullName: z.string().min(1).optional(),
	phoneNumber: z.string().optional(),
	role: RoleSchema.optional(),
	emailConfirmed: z.boolean().optional(),
	phoneNumberConfirmed: z.boolean().optional(),
	twoFactorEnabled: z.boolean().optional(),
	lockoutEnabled: z.boolean().optional(),
	lockoutEnd: z.string().optional(),
	active: z.boolean().optional(),
	tenantId: z.string().optional(),
	permissions: z.array(z.nativeEnum(USER_PERMISSIONS as unknown as Record<string, string>)).optional(),
	workstationIds: z.array(z.number()).optional(),
	newPassword: z.string().min(8, "Password must be at least 8 characters").optional(),
	confirmPassword: z.string().optional(),
})
	.superRefine((data, ctx) => {
		const hasPasswordChange = !!data.newPassword || !!data.confirmPassword
		if (!hasPasswordChange) {
			return
		}

		if (!data.newPassword) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "New password is required when changing password",
				path: ["newPassword"],
			})
		}

		if (!data.confirmPassword) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Confirm password is required when changing password",
				path: ["confirmPassword"],
			})
		} else if (data.newPassword && data.confirmPassword !== data.newPassword) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "New password and confirm password do not match",
				path: ["confirmPassword"],
			})
		}
	})
	.refine((data) => Object.values(data).some((v) => v !== undefined), {
		message: "No changes to update",
	})

export type CreateUserPayload = z.infer<typeof CreateUserSchema>
export type UpdateUserPayload = z.infer<typeof UpdateUserSchema>


